import { addAllToRecord, addToRecord, objectWithoutKeys } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { getGraphqlApiErrorReason } from '../../api/errors';
import { GraphqlErrorReason } from '../../api/types';
import { CancelEventFulfilledAction } from '../../domains/cancelEvent/actions.types';
import { CreateEventFulfilledAction } from '../../domains/createEvent/actions.types';
import { CreateGroupCalendarInviteForBroadcastFulfilledAction } from '../../domains/createGroupCalendarInviteForBroadcast/actions.types';
import { EditEventFulfilledAction, EditRestrictedPostingFulfilledAction } from '../../domains/editEvent/actions.types';
import { EventLoadByBroadcastIdFulfilledAction } from '../../domains/eventLoadByBroadcastId/actions.types';
import { EventRestrictedPostingUpdateAction } from '../../domains/eventRestrictedPosting/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import { LiveEventDownloadIcsFileFulfilledAction } from '../../domains/liveEventDownloadIcsFile/actions.types';
import { LiveEventLoadByGroupIdFulfilledAction } from '../../domains/liveEventLoadByGroupId/actions.types';
import { LiveEventLoadByNetworkFulfilledAction } from '../../domains/liveEventsLoadByNetwork/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import { PublishEventMessageRejectedAction } from '../../domains/publishers/actions.types';
import {
  ThreadLoadFulfilledAction,
  ThreadLoadNewerRepliesFulfilledAction,
  ThreadLoadOlderRepliesFulfilledAction,
} from '../../domains/threadLoad/actions.types';

import { LiveEvent, LiveEvents } from './types';

export type LiveEventAction =
  | CancelEventFulfilledAction
  | CreateEventFulfilledAction
  | CreateGroupCalendarInviteForBroadcastFulfilledAction
  | EditEventFulfilledAction
  | EditRestrictedPostingFulfilledAction
  | EventLoadByBroadcastIdFulfilledAction
  | LiveEventDownloadIcsFileFulfilledAction
  | LiveEventLoadByGroupIdFulfilledAction
  | LiveEventLoadByNetworkFulfilledAction
  | ThreadLoadFulfilledAction
  | ThreadLoadNewerRepliesFulfilledAction
  | ThreadLoadOlderRepliesFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | EventRestrictedPostingUpdateAction
  | PublishEventMessageRejectedAction;

const initialState: LiveEvents = {};

const updateRestrictedPosting = (state: LiveEvents, broadcastId: string, isThreadStarterRestricted: boolean) =>
  addToRecord(state, broadcastId, {
    ...state[broadcastId],
    isThreadStarterRestricted,
  });

const updateCalendarFile = (state: LiveEvents, broadcastId: LiveEvent['id'], icsBlobLink: string) =>
  addToRecord(state, broadcastId, {
    ...state[broadcastId],
    icsBlobLink,
  });

const stateWithFulfilled = (
  state: LiveEvents,
  action: Exclude<
    LiveEventAction,
    | CancelEventFulfilledAction
    | EventRestrictedPostingUpdateAction
    | LiveEventDownloadIcsFileFulfilledAction
    | PublishEventMessageRejectedAction
  >
) => addAllToRecord(state, action.payload.entities.liveEvents);

const reducer: Reducer<LiveEvents, LiveEventAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CANCEL_EVENT.FULFILLED':
      return objectWithoutKeys(state, [action.payload.broadcastId]);
    case 'EVENT.LOAD_BY_BROADCAST_ID_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'LIVE_EVENT.LOAD_BY_GROUP_ID_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'CREATE_EVENT.FULFILLED':
      return stateWithFulfilled(state, action);
    case 'CREATE_GROUP_CALENDAR_INVITE_FOR_BROADCAST.FULFILLED':
      return stateWithFulfilled(state, action);
    case 'EDIT_EVENT.FULFILLED':
      return stateWithFulfilled(state, action);
    case 'EDIT_RESTRICTED_POSTING.FULFILLED':
      return stateWithFulfilled(state, action);
    case 'LIVE_EVENT.LOAD_BY_NETWORK_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'THREAD.LOAD_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'THREAD.LOAD_NEWER_REPLIES_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'THREAD.LOAD_OLDER_REPLIES_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'FEED.LOAD_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return stateWithFulfilled(state, action);
    case 'EVENT.UPDATE_RESTRICTED_POSTING':
      return updateRestrictedPosting(state, action.payload.broadcastId, action.payload.isPostingRestricted);
    case 'LIVE_EVENT.DOWNLOAD_ICS_FILE_FULFILLED':
      return updateCalendarFile(state, action.payload.broadcastId, action.payload.icsBlobLink);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_REJECTED':
      if (getGraphqlApiErrorReason(action.payload.error) === GraphqlErrorReason.RestrictedPostingInEvent) {
        return updateRestrictedPosting(state, action.payload.broadcastId, true);
      }

      return state;
    default:
      return state;
  }
};

export default reducer;
